import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

// Campaign API — lets a store (or a system acting for it) send SMS and email to
// its customers through an API, one at a time or as a batch, from the
// location's campaign number pool (the separate campaign-api service, design
// v0.3). Credentials belong to
// a location and are issued by a super admin only. Every call here goes
// through dashboard-api's /admin/campaign-api proxy, which holds the
// X-Admin-Key; the browser never talks to campaign-api's admin routes.

export interface CampaignApiClient {
  id: string;
  client_id: string;
  tenant_id: string;
  location_id: string | null;
  label: string | null;
  scopes: string[];
  env: 'test' | 'live';
  status: 'active' | 'revoked';
  created_at: string;
  secret_rotated_at: string | null;
  previous_secret_expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
}

// Returned ONCE, at creation or rotation. campaign-api stores only a hash, so
// this is the one moment the secret exists anywhere.
export interface CampaignApiClientWithSecret extends CampaignApiClient {
  client_secret: string;
}

const clientsKey = (tenantId: string, locationId: string) => ['admin', 'campaign-api', 'clients', tenantId, locationId];

export function useCampaignApiClients(tenantId: string, locationId: string, enabled = true) {
  return useQuery({
    queryKey: clientsKey(tenantId, locationId),
    queryFn: () =>
      api.get<{ data: CampaignApiClient[] }>('/admin/campaign-api/clients', {
        tenant_id: tenantId,
        location_id: locationId,
      }),
    enabled: enabled && !!tenantId && !!locationId,
    retry: false,
  });
}

export function useCreateCampaignApiClient(tenantId: string, locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label: string; env: 'test' | 'live' }) =>
      api.post<CampaignApiClientWithSecret>('/admin/campaign-api/clients', {
        tenant_id: tenantId,
        location_id: locationId,
        ...input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: clientsKey(tenantId, locationId) }),
  });
}

export function useRotateCampaignApiClient(tenantId: string, locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<CampaignApiClientWithSecret>(`/admin/campaign-api/clients/${id}/rotate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: clientsKey(tenantId, locationId) }),
  });
}

export function useRevokeCampaignApiClient(tenantId: string, locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<CampaignApiClient>(`/admin/campaign-api/clients/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: clientsKey(tenantId, locationId) }),
  });
}

export interface CampaignApiStatusCounts {
  queued: number;
  sent: number;
  delivered: number;
  failed: number;
  suppressed: number;
  cancelled: number;
}

export interface CampaignApiStatsCounts {
  messages: number;
  batch_messages: number;
  sms: CampaignApiStatusCounts & { segments: number };
  email: CampaignApiStatusCounts;
}

export interface CampaignApiStatsRow extends CampaignApiStatsCounts {
  period: string | null;
  client?: { id: string; client_id: string | null; label: string | null; env: 'test' | 'live' | null };
}

/** One month (YYYY-MM, in the store's time zone) per credential, every
 *  outcome counted, test sends included. */
export function useCampaignApiStats(tenantId: string, locationId: string, month: string, timeZone: string) {
  return useQuery({
    queryKey: ['admin', 'campaign-api', 'stats', tenantId, locationId, month, timeZone],
    queryFn: () =>
      api.get<{ from: string; to: string; totals: CampaignApiStatsCounts; data: CampaignApiStatsRow[] }>(
        '/admin/campaign-api/stats',
        { tenant_id: tenantId, location_id: locationId, month, tz: timeZone, group_by: 'client' },
      ),
    enabled: !!tenantId && !!locationId && !!month,
    retry: false,
  });
}

/** The current month (YYYY-MM) on the store's clock. */
export function currentMonth(timeZone: string, now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit' }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}`;
}
